import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { ArrowRight, Lock } from "lucide-react";
import { PhoneFrame } from "@/components/landing/TenantDemo";
import { prefersReducedMotion } from "@/components/landing/stage";
import { linkProps } from "@/components/landing/destinations";
import { cn } from "@/lib/utils";

const MEDIA = "/images/landing/features";
const H2 = "text-balance font-semibold tracking-tight";

const More = ({ to, className, testid, children }) => {
  const props = linkProps(to);
  const cls = cn("group inline-flex w-fit items-center gap-1.5 text-[15px] font-medium", className);
  const inner = <>{children}<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" /></>;
  return props.to
    ? <Link {...props} className={cls} data-testid={testid}>{inner}</Link>
    : <a {...props} className={cls} data-testid={testid}>{inner}</a>;
};

/** A muted, looping recording that plays only while it's on screen. */
const DemoVideo = ({ name, label, width, height }) => {
  const ref = useRef(null);
  const still = useMemo(() => prefersReducedMotion(), []);
  useEffect(() => {
    const v = ref.current;
    if (still || !v) return undefined;
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause()), { threshold: 0.3 });
    io.observe(v);
    return () => io.disconnect();
  }, [still, name]);
  return (
    <video key={name} ref={ref} muted loop playsInline preload="metadata" poster={`${MEDIA}/${name}.jpg`} aria-label={label}
      width={width} height={height} controls={still} className="block h-auto w-full" data-testid={`landing-video-${name}`}>
      <source src={`${MEDIA}/${name}.webm`} type="video/webm" />
      <source src={`${MEDIA}/${name}.mp4`} type="video/mp4" />
    </video>
  );
};

const useDark = () => useTheme().resolvedTheme === "dark";

const SameDay = ({ agentTo }) => (
  <section id="product" className="scroll-mt-28" data-testid="feature-same-day">
    <div className="grid gap-6 md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] md:items-end md:gap-16">
      <h2 className={`${H2} text-[34px] leading-[1.05] md:text-[56px]`}>A reference the same day they apply.</h2>
      <div className="flex flex-col gap-5 md:pb-2">
        <p className="text-base leading-relaxed text-muted-foreground md:text-lg">Landin reads every document in seconds, checks it against your criteria and gives you one page to accept or decline.</p>
        <More to={agentTo} testid="feature-same-day-link">Start referencing</More>
      </div>
    </div>
    <div className="landin-backdrop mt-12 rounded-3xl px-3 pt-3 sm:px-10 sm:pt-10 md:mt-16 lg:px-20 lg:pt-16">
      <div className="mx-auto max-w-4xl overflow-hidden rounded-t-xl border border-b-0 bg-background shadow-[0_24px_60px_-28px_rgb(0_0_0/0.4)]">
        <div className="flex h-8 items-center gap-1.5 border-b bg-panel px-3">
          {[0, 1, 2].map((i) => <span key={i} className="size-2.5 rounded-full bg-muted-foreground/25" />)}
          <span className="mx-auto hidden h-5 w-64 items-center justify-center gap-1 rounded-md bg-background text-[11px] text-muted-foreground ring-1 ring-border sm:flex">
            <Lock className="size-2.5" /> landin.app/applicants
          </span>
        </div>
        <AgentVideo />
      </div>
    </div>
  </section>
);

const AgentVideo = () => (
  <DemoVideo name={useDark() ? "agent-review-dark" : "agent-review"} label="A letting agent reviews an applicant in Landin" width={1600} height={1000} />
);

const Fraud = () => {
  const dark = useDark();
  return (
  <section id="fraud-checks" className="grid scroll-mt-28 items-center gap-10 rounded-3xl bg-neutral-950 px-6 py-12 text-white sm:px-10 md:py-16 lg:grid-cols-[minmax(0,4fr)_minmax(0,5fr)] lg:gap-16 lg:px-16 lg:py-20" data-testid="feature-fraud">
    <div>
      <p className="text-sm font-medium text-[hsl(var(--brand))]">Fraud checks</p>
      <h2 className={`${H2} mt-4 text-[28px] leading-[1.1] md:text-[40px]`}>Catches the payslip that's been edited.</h2>
      <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">Every payslip is checked for signs of editing, and its net pay is matched against what actually arrived in the bank. Anything off is flagged, with the evidence.</p>
    </div>
    <img src={`${MEDIA}/crop-fraud${dark ? "-dark" : ""}.webp`} width={1488} height={930} loading="lazy" className="block h-auto w-full rounded-xl ring-1 ring-white/10"
      alt="Each salary payment is 31% lower than the payslip says, so all three are flagged." data-testid="feature-fraud-image" />
  </section>
  );
};

const POINTS = [
  ["The same rules for everyone.", "Set the income multiple, guarantor income, payslips and bank tolerance per property. Applicants can read them before they apply."],
  ["A blind first look.", "No name, photo, age, nationality or income source until you proceed. You only ever see the payments that prove income, never spending."],
  ["Decisions in one click.", "Ask for a missing document, or accept and send an email Landin has already written. Your team sees it all in one place."],
];

const Fair = () => (
  <section id="fair" className="scroll-mt-28" data-testid="feature-points">
    <h2 className={`${H2} max-w-2xl text-[28px] leading-[1.1] md:text-[40px]`}>Fair, consistent, and quick to act on.</h2>
    <div className="mt-12 grid gap-4 md:mt-16 md:grid-cols-3">
      {POINTS.map(([title, text], i) => (
        <div key={title} className="flex flex-col justify-between gap-8 rounded-2xl border bg-panel p-7 md:min-h-[240px] md:p-8" data-testid={`feature-point-${i}`}>
          <h3 className="text-lg font-medium leading-snug">{title}</h3>
          <p className="text-[15px] leading-relaxed text-muted-foreground">{text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Tenants = () => {
  const dark = useDark();
  return (
    <section id="tenants" className="grid scroll-mt-28 items-center overflow-hidden rounded-3xl bg-[hsl(var(--brand)/0.45)] md:grid-cols-2" data-testid="feature-tenant">
      <div className="order-2 flex justify-center px-6 pb-12 md:order-none md:px-10 md:py-16">
        <PhoneFrame className="max-w-[280px]" testid="feature-tenant-phone">
          <DemoVideo name={dark ? "tenant-flow-dark" : "tenant-flow"} label="A tenant applies on their phone" width={780} height={1588} />
        </PhoneFrame>
      </div>
      <div className="px-6 py-12 md:py-16 md:pr-14 lg:pr-20">
        <p className="text-sm font-medium text-foreground/60">For tenants</p>
        <h2 className={`${H2} mt-4 text-[28px] leading-[1.1] md:text-[40px]`}>Apply from your phone, one step at a time.</h2>
        <p className="mt-5 max-w-md text-base leading-relaxed text-foreground/70">Each document is checked as it's uploaded. If a payslip is for the wrong month, they're told which one to send instead, and they can save and come back later.</p>
        <More to="/tenant" className="mt-8" testid="feature-tenant-link">Continue an application</More>
      </div>
    </section>
  );
};

/** The sections below the hero, each reachable from the top bar. */
export const Features = ({ agentTo }) => (
  <div className="flex flex-col gap-32 md:gap-48" data-testid="landing-features">
    <SameDay agentTo={agentTo} />
    <Fraud />
    <Fair />
    <Tenants />
  </div>
);
